import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { useLocation, useNavigate, Navigate } from 'react-router-dom'
import { useContext, useState, useEffect } from 'react'
import ClientContext from 'contexts/ClientContext'
import Button from 'components/common/Button/Button'
import Input from 'components/common/Input/Input'
import style from './ContactForm.module.scss'
import PageSubtitle from 'components/common/PageSubtitle/PageSubtitle'
import PageTitle from 'components/common/PageTitle/PageTitle'
import Lock from 'components/assets/icons/Lock'
import Datepicker from 'components/common/Datepicker/Datepicker'
import { isInputError } from 'helpers/form'
import api from 'helpers/api'
import { birthdateToPickerDate, pickerDateToBirthdate } from 'helpers/birthdate'

const ContactForm = () => {
  const { t, i18n: { language } } = useTranslation()
  const navigate = useNavigate()
  const { state } = useLocation()
  const { color, colabelNeutralDark } = useContext(ClientContext)
  const [ contract, setContract ] = useState(state?.contract)
  const { isSomeoneElse, claimsClientInfo } = state || {}

  const getDefaultValue = field => (!isSomeoneElse && contract && contract[field]) || ''
  const defaultBirthdate = birthdateToPickerDate(getDefaultValue('birthdate'))
  const { moralClient, modificationInformationDisallowed } = state.contract.contractType

  const {
    control,
    register,
    formState: { errors, isValid },
    handleSubmit,
    trigger
  } = useForm({
    criteriaMode: 'all',
    delayError: 500,
    defaultValues: {
      clientRelationship: '',
      firstname: getDefaultValue('firstname'),
      lastname: getDefaultValue('lastname'),
      birthdate: defaultBirthdate || new Date(),
      email: getDefaultValue('email'),
      phone: getDefaultValue('phone'),
      ...(moralClient
        ? {
          organizationName: getDefaultValue('organizationName'),
          headquarterAddress: getDefaultValue('headquarterAddress'),
          headquarterZipcode: getDefaultValue('headquarterZipcode'),
          headquarterCity: getDefaultValue('headquarterCity'),
          headquarterCountry: getDefaultValue('headquarterCountry'),
          companyImmatriculation: getDefaultValue('companyImmatriculation'),
        }
        : {
          placeOfBirth: getDefaultValue('placeOfBirth'),
          countryOfBirth: getDefaultValue('countryOfBirth'),
          address: getDefaultValue('address'),
          zipcode: getDefaultValue('zipcode'),
          city: getDefaultValue('city'),
          country: getDefaultValue('country'),
          additionalAddress: getDefaultValue('additionalAddress'),
        }
      )
    },
    mode: 'onTouched',
  })

  const errorFields = Object.keys(errors)

  const lock = (field) => {
    const shouldLock = !isSomeoneElse && modificationInformationDisallowed && !!getDefaultValue(field)
    return {
      readOnly: shouldLock,
      suffix: shouldLock && <Lock styles={color && {fill: colabelNeutralDark}} />
    }
  }

  useEffect(() => {
    const getContract = async () => {
      try {
        const res = await api.get({ url: `/adhesions/${contract._id}/client` })
        if (res?.adhesion) setContract(res.adhesion)
        trigger()
      } catch (error) {
        console.log('Fetch contracts error')
      }
    }
    getContract()
  }, [language, contract._id, trigger])

  const onSubmit = handleSubmit(async data => {
    navigate('../circumstances', {
      state: {
        ...state,
        step: 3,
        claim: { ...data, birthdate: pickerDateToBirthdate(data.birthdate) }
      },
    })
  })


  return claimsClientInfo === false
  ? <Navigate
      to={'/claims/new/circumstances'}
      state={{ ...state, step: 3 }}
      replace
    />
  : (
    <>
      <PageTitle
        text={
          isSomeoneElse ? t('claim:applySteps.contacts.someoneElse.title') : t('claim:applySteps.contacts.you.title')
        }
      />
      <PageSubtitle
        text={
          isSomeoneElse
            ? t('claim:applySteps.contacts.someoneElse.subtitle')
            : t('claim:applySteps.contacts.you.subtitle')
        }
      />

      <form className={style.form} onSubmit={onSubmit}>
        <div className={style.inputs}>
          {isSomeoneElse && (
            <Input
              className={style.input}
              register={register('clientRelationship', { required: true })}
              label={t('claim:applySteps.contacts.someoneElse.clientRelationship.label')}
              placeholder={t('claim:applySteps.contacts.someoneElse.clientRelationship.placeholder')}
            />
          )}
          <Input
            className={style.input}
            error={isInputError(errorFields, 'firstname')}
            register={register('firstname', { required: true })}
            label={t('common:form.firstname.label')}
            placeholder={`${t(isSomeoneElse ? 'common:their' : 'common:your')} ${t(
              'common:form.firstname.label',
            ).toLowerCase()}`}
            readOnly={!isSomeoneElse}
            suffix={!isSomeoneElse && <Lock styles={color && {fill: colabelNeutralDark}} />}
          />
          <Input
            className={style.input}
            error={isInputError(errorFields, 'lastname')}
            register={register('lastname', { required: true })}
            label={t('common:form.lastname.label')}
            placeholder={`${t(isSomeoneElse ? 'common:their' : 'common:your')} ${t(
              'common:form.lastname.label',
            ).toLowerCase()}`}
            readOnly={!isSomeoneElse}
            suffix={!isSomeoneElse && <Lock styles={color && {fill: colabelNeutralDark}} />}
          />
          <div className={classNames(style.datepicker, style.input)}>
            <div className="input-label" style={color && {color: colabelNeutralDark}}>{t('common:form.birthdate.label')}</div>
            <Controller
              control={control}
              name="birthdate"
              render={({ field: { onChange, value } }) => (
                <Datepicker
                  className={style.input}
                  error={isInputError(errorFields, 'birthdate')}
                  onChange={onChange}
                  placeholder={t(
                    isSomeoneElse ? 'common:form.birthdate.placeholder2' : 'common:form.birthdate.placeholder',
                  )}
                  register={register('birthdate', { required: true })}
                  value={value}
                />
              )}
            />
            {!isSomeoneElse && modificationInformationDisallowed && getDefaultValue('birthdate') && <div className={style.suffix}>
              <Lock styles={color && {fill: colabelNeutralDark}} />
            </div>}
          </div>
          {moralClient === false && <>
            <Input
              className={style.input}
              error={isInputError(errorFields, 'placeOfBirth')}
              register={register('placeOfBirth', { required: true })}
              label={t('common:form.placeOfBirth.label')}
              placeholder={`${t(isSomeoneElse ? 'common:their' : 'common:your')} ${t(
                'common:form.placeOfBirth.label',
              ).toLowerCase()}`}
              {...lock('placeOfBirth')}
            />
            <Input
              className={style.input}
              error={isInputError(errorFields, 'countryOfBirth')}
              register={register('countryOfBirth', { required: true })}
              label={t('common:form.countryOfBirth.label')}
              placeholder={`${t(isSomeoneElse ? 'common:their' : 'common:your')} ${t(
                'common:form.countryOfBirth.label',
              ).toLowerCase()}`}
              {...lock('countryOfBirth')}
            />
            <Input
              className={style.input}
              error={isInputError(errorFields, 'address')}
              register={register('address', { required: true })}
              label={t('common:form.address.label')}
              placeholder={`${t(isSomeoneElse ? 'common:their' : 'common:your')} ${t(
                'common:form.address.label',
              ).toLowerCase()}`}
              {...lock('address')}
            />
            <Input
              className={style.input}
              error={isInputError(errorFields, 'zipcode')}
              register={register('zipcode', { required: true })}
              label={t('common:form.zipCode.label')}
              placeholder={`${t(isSomeoneElse ? 'common:their' : 'common:your')} ${t(
                'common:form.zipCode.label',
              ).toLowerCase()}`}
              {...lock('zipcode')}
            />
            <Input
              className={style.input}
              error={isInputError(errorFields, 'city')}
              register={register('city', { required: true })}
              label={t('common:form.city.label')}
              placeholder={t(isSomeoneElse ? 'common:form.city.placeholder2' : 'common:form.city.placeholder')}
              {...lock('city')}
            />
            <Input
              className={style.input}
              error={isInputError(errorFields, 'country')}
              register={register('country', { required: true })}
              label={t('common:form.country.label')}
              placeholder={`${t(isSomeoneElse ? 'common:their' : 'common:your')} ${t(
                'common:form.country.label',
              ).toLowerCase()}`}
              {...lock('country')}
            />
            <Input
              className={style.input}
              error={isInputError(errorFields, 'additionalAddress')}
              register={register('additionalAddress')}
              label={t('common:form.additionalAddress.label')}
              placeholder={t('common:form.additionalAddress.placeholder')}
              readOnly={!isSomeoneElse && modificationInformationDisallowed}
              suffix={(!isSomeoneElse && modificationInformationDisallowed) && <Lock styles={color && {fill: colabelNeutralDark}} />}
            />
          </>}
          <Input
            className={style.input}
            error={isInputError(errorFields, 'email')}
            register={register('email', { required: true })}
            label={t('common:form.email.label')}
            placeholder={`${t(isSomeoneElse ? 'common:their' : 'common:your')} ${t('common:form.email.placeholder')}`}
            readOnly={!isSomeoneElse}
            suffix={!isSomeoneElse && <Lock styles={color && {fill: colabelNeutralDark}} />}
          />
          <Input
            className={style.input}
            error={isInputError(errorFields, 'phone')}
            register={register('phone', { required: true })}
            label={t('common:form.phone.label')}
            placeholder={`${t(isSomeoneElse ? 'common:their' : 'common:your')} ${t(
              'common:form.phone.placeholder',
            ).toLowerCase()}`}
            {...lock('phone')}
          />
          {moralClient !== false && <>
            <Input
              className={style.input}
              error={isInputError(errorFields, 'organizationName')}
              register={register('organizationName', { required: true })}
              label={t('common:form.organizationName.label')}
              placeholder={t('common:form.organizationName.placeholder')}
              {...lock('organizationName')}
            />
            <Input
              className={style.input}
              error={isInputError(errorFields, 'headquarterAddress')}
              register={register('headquarterAddress', { required: true })}
              label={t('common:form.headquarterAddress.label')}
              placeholder={t('common:form.headquarterAddress.placeholder')}
              {...lock('headquarterAddress')}
            />
            <Input
              className={style.input}
              error={isInputError(errorFields, 'headquarterZipcode')}
              register={register('headquarterZipcode', { required: true })}
              label={t('common:form.headquarterZipcode.label')}
              placeholder={t('common:form.headquarterZipcode.placeholder')}
              {...lock('headquarterZipcode')}
            />
            <Input
              className={style.input}
              error={isInputError(errorFields, 'headquarterCity')}
              register={register('headquarterCity', { required: true })}
              label={t('common:form.headquarterCity.label')}
              placeholder={t('common:form.headquarterCity.placeholder')}
              {...lock('headquarterCity')}
            />
            <Input
              className={style.input}
              error={isInputError(errorFields, 'headquarterCountry')}
              register={register('headquarterCountry', { required: true })}
              label={t('common:form.headquarterCountry.label')}
              placeholder={t('common:form.headquarterCountry.placeholder')}
              {...lock('headquarterCountry')}
            />
            <Input
              className={style.input}
              error={isInputError(errorFields, 'companyImmatriculation')}
              register={register('companyImmatriculation', { required: true })}
              label={t('common:form.companyImmatriculation.label')}
              placeholder={t('common:form.companyImmatriculation.placeholder')}
              {...lock('companyImmatriculation')}
            />
          </>}
        </div>

        <Button
          className={style.button}
          label={t('claim:submit')}
          onClick={handleSubmit}
          type="primary"
          disabled={!isValid}
        />
      </form>
    </>
  )
}

export default ContactForm
